/**
 * Step Input Resolution
 *
 * Resolves input for workflow steps based on configuration and dependencies.
 * Handles template interpolation and dependency output merging.
 *
 * @packageDocumentation
 */

import type { WorkflowStep } from '../types/workflow';
import type { ExecutionContext } from '../types/execution';
import { interpolateValue } from '../utils/interpolate';

/**
 * Resolves the final input for a workflow step.
 *
 * Resolution order:
 * 1. If step.input exists → use it (with template interpolation)
 * 2. Else if step.prompt exists → use prompt (with template interpolation)
 * 3. Else → use context.initialInput
 *
 * Additionally, includes dependency outputs in the resolved input.
 *
 * @param step - The workflow step to resolve input for
 * @param context - Execution context with step outputs
 * @returns Resolved input ready for adapter execution
 *
 * @example
 * ```typescript
 * const step = {
 *   stepName: 'summarize',
 *   model: 'gpt-4o',
 *   prompt: 'Summarize: {{extract.output}}',
 *   dependsOn: ['extract']
 * };
 *
 * const input = resolveStepInput(step, context);
 * // Returns: {
 * //   prompt: 'Summarize: {...extracted data...}',
 * //   dependencies: { extract: {...} }
 * // }
 * ```
 */
export function resolveStepInput(step: WorkflowStep, context: ExecutionContext): any {
  // Build context map for interpolation
  const interpolationContext = new Map<string, any>();

  // Add initial input as "input" so templates like {{input.field}} work
  if (context.initialInput !== undefined && context.initialInput !== null) {
    interpolationContext.set('input', context.initialInput);
  }

  for (const [stepName, output] of context.stepOutputs.entries()) {
    interpolationContext.set(stepName, output);
  }

  // Determine base input
  let baseInput: any;

  if (step.input !== undefined) {
    // Use explicit step input
    baseInput = step.input;
  } else if (step.prompt !== undefined) {
    // Use prompt as input
    baseInput = step.prompt;
  } else {
    // Use initial workflow input
    baseInput = context.initialInput;
  }

  // Interpolate templates in the base input
  const resolvedInput = interpolateValue(baseInput, interpolationContext);

  // Include dependency outputs if present
  if (step.dependsOn && step.dependsOn.length > 0) {
    const dependencies: Record<string, any> = {};

    for (const depName of step.dependsOn) {
      const depOutput = context.getOutput(depName);
      if (depOutput !== undefined) {
        dependencies[depName] = depOutput;
      }
    }

    // Return structured input with dependencies
    return {
      input: resolvedInput,
      dependencies,
    };
  }

  // No dependencies, return input directly
  return resolvedInput;
}

/**
 * Checks if all dependencies for a step have been executed.
 *
 * @param step - The workflow step to check
 * @param context - Execution context with step outputs
 * @returns True if all dependencies have outputs
 */
export function areDependenciesReady(step: WorkflowStep, context: ExecutionContext): boolean {
  if (!step.dependsOn || step.dependsOn.length === 0) {
    return true;
  }

  return step.dependsOn.every((depName) => context.hasOutput(depName));
}
